"use client";
// app/context/PermissionContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import api from "@/app/axios/axiosInstance";
import {
  PERMISSION,
  MODULE,
  NUM_MODULES,
  hasPerm,
} from "@/app/permissions/permissions";

export interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface RolePrivilege {
  privilege_id?: string;
  privilege_name?: string;
  permission: number;
}

interface RoleData {
  id: string;
  name: string;
  privileges: RolePrivilege[];
}

interface ApiPrivilege {
  id: string;
  index: number;
  name: string;
}

interface PermissionContextValue {
  profile: Profile | null;
  permsStr: string;
  moduleIndex: Record<string, number>;
  loading: boolean;
  can: (nameOrModule: string | number, required?: number) => boolean;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

const normalizeRole = (s: string | null | undefined) =>
  (s ?? "").trim().toLowerCase();

/** "Mat_Pinjam" / "mat-pinjam" / "MatPinjam" → "matpinjam" */
const normalizeKey = (s: string | null | undefined) =>
  (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// SESUAIKAN key dengan axiosInstance-mu ("token")
const hasToken = () =>
  typeof window !== "undefined" && Boolean(localStorage.getItem("token"));

// PENTING: daftar ini harus sama persis dengan PUBLIC_PATHS di AuthGuard.tsx.
// TODO: pindahkan ke satu file bersama (mis. app/config/publicPaths.ts) supaya
// dua daftar ini tidak bisa diam-diam jadi tidak sinkron.
const PUBLIC_PATHS = [
  "/",
  "/pages/login",
  "/pages/reset-request",
  "/pages/reset-password",
];

const isPublicPath = (pathname: string | null) =>
  !!pathname &&
  PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

/**
 * Peta nama → index bawaan dari konstanta MODULE (frontend). Dipakai sebagai
 * fallback bila katalog /admin/privileges tidak bisa diakses (user non-admin)
 * atau nama privilege di backend beda format (snake_case vs PascalCase).
 */
const MODULE_INDEX_FALLBACK: Record<string, number> = Object.fromEntries(
  Object.entries(MODULE).map(([k, v]) => [normalizeKey(k), v]),
);

/**
 * FIX: sebelumnya panjang permsStr = index privilege TERBESAR di katalog
 * /admin/privileges, dan pencocokan privilege role hanya lewat
 * `privilege_name`. Akibatnya:
 *  - Kalau katalog/role belum memuat MatPinjam (22) & MatPengembalian (23),
 *    string cuma 21 karakter → hasPerm() langsung false → menu
 *    Usulan Peminjaman/Pengembalian hilang dari sidebar.
 *  - Kalau respons role hanya membawa `privilege_id` (seperti yang dipakai
 *    usePermissions.ts), `privilege_name` undefined → `.toLowerCase()` crash
 *    atau privilege tidak terbaca sama sekali.
 *
 * Sekarang: panjang minimal NUM_MODULES, pencocokan lewat privilege_id dulu,
 * lalu nama (dinormalisasi), lalu fallback konstanta MODULE.
 */
function buildPermsStr(
  rolePrivileges: RolePrivilege[],
  allPrivileges: ApiPrivilege[],
): { permsStr: string; moduleIndex: Record<string, number> } {
  const moduleIndex: Record<string, number> = { ...MODULE_INDEX_FALLBACK };
  const indexById: Record<string, number> = {};
  let maxIndex = NUM_MODULES;

  for (const p of allPrivileges) {
    if (!p || typeof p.index !== "number") continue;
    if (p.name) {
      moduleIndex[p.name.toLowerCase()] = p.index;
      moduleIndex[normalizeKey(p.name)] = p.index;
    }
    if (p.id) indexById[p.id] = p.index;
    if (p.index > maxIndex) maxIndex = p.index;
  }

  const nibbles = new Array<string>(maxIndex).fill("0");

  for (const rp of rolePrivileges) {
    const idx =
      (rp.privilege_id ? indexById[rp.privilege_id] : undefined) ??
      (rp.privilege_name
        ? moduleIndex[rp.privilege_name.toLowerCase()] ??
          moduleIndex[normalizeKey(rp.privilege_name)]
        : undefined);

    if (idx && idx >= 1 && idx <= maxIndex) {
      nibbles[idx - 1] = (Number(rp.permission) & 0xf).toString(16);
    }
  }

  return { permsStr: nibbles.join(""), moduleIndex };
}

/**
 * Coba baca privilege milik user sendiri dari GET /profile/privileges
 * (endpoint yang bisa diakses SEMUA role, bukan hanya admin). Toleran
 * terhadap beberapa bentuk respons:
 *  - string hex langsung ("f2f...")  atau { perms / perms_str / permissions: "..." }
 *  - array [{ privilege_id?, privilege_name? | name?, index?, permission }]
 * Mengembalikan null bila bentuknya tidak dikenali → pakai jalur /admin/*.
 */
function parseOwnPrivileges(
  raw: any,
  knownCatalog: ApiPrivilege[] = [],
): { permsStr: string; moduleIndex: Record<string, number> } | null {
  const body = raw?.data ?? raw;

  const hex =
    typeof body === "string"
      ? body
      : typeof body?.perms === "string"
        ? body.perms
        : typeof body?.perms_str === "string"
          ? body.perms_str
          : typeof body?.permissions === "string"
            ? body.permissions
            : null;

  if (hex && /^[0-9a-f]+$/i.test(hex.trim())) {
    return {
      permsStr: hex.trim().padEnd(NUM_MODULES, "0"),
      moduleIndex: { ...MODULE_INDEX_FALLBACK },
    };
  }

  const list: any[] | null = Array.isArray(body)
    ? body
    : Array.isArray(body?.privileges)
      ? body.privileges
      : null;
  if (!list) return null;

  const catalog: ApiPrivilege[] = [...knownCatalog];
  const rolePrivs: RolePrivilege[] = [];
  for (const item of list) {
    const name = item?.privilege_name ?? item?.name;
    const index = item?.index ?? item?.privilege_index;
    const id = item?.privilege_id ?? item?.id;
    if (typeof index === "number") catalog.push({ id: id ?? "", index, name: name ?? "" });
    rolePrivs.push({
      privilege_id: id,
      privilege_name: name,
      permission: Number(item?.permission ?? 0),
    });
  }
  const built = buildPermsStr(rolePrivs, catalog);
  // Ada privilege di respons tapi tidak satupun terpetakan ke index →
  // bentuk respons tidak dikenali; biarkan jalur /admin/* yang menangani.
  if (list.length > 0 && /^0*$/.test(built.permsStr)) return null;
  return built;
}

export function PermissionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [permsStr, setPermsStr] = useState<string>("");
  const [moduleIndex, setModuleIndex] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const pathname = usePathname();
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  // Guards against overlapping refresh() calls (lihat penjelasan sebelumnya):
  // hanya request yang paling terakhir dimulai yang boleh commit state.
  const requestIdRef = useRef(0);

  const clear = useCallback(() => {
    requestIdRef.current++;
    setProfile(null);
    setPermsStr("");
    setModuleIndex({});
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!hasToken() || isPublicPath(pathnameRef.current)) {
      clear();
      return;
    }

    setLoading(true);
    try {
      const profileRes = await api.get("/profile");
      if (requestId !== requestIdRef.current) return;
      const me: Profile = profileRes.data?.data ?? profileRes.data;
      setProfile(me);

      // 1) Jalur utama: privilege milik user sendiri (semua role bisa akses)
      const [ownRes, rolesRes, privsRes] = await Promise.allSettled([
        api.get("/profile/privileges"),
        api.get("/admin/roles"),
        api.get("/admin/privileges"),
      ]);
      if (requestId !== requestIdRef.current) return;

      const allPrivileges: ApiPrivilege[] =
        privsRes.status === "fulfilled"
          ? privsRes.value.data?.data ?? privsRes.value.data ?? []
          : [];

      let result: { permsStr: string; moduleIndex: Record<string, number> } | null =
        null;

      if (ownRes.status === "fulfilled") {
        result = parseOwnPrivileges(ownRes.value.data, allPrivileges);
        // Lengkapi moduleIndex dengan nama katalog bila tersedia
        if (result && allPrivileges.length) {
          const extra = buildPermsStr([], allPrivileges).moduleIndex;
          result.moduleIndex = { ...extra, ...result.moduleIndex };
        }
      }

      // 2) Fallback: cocokkan role user di /admin/roles (hanya admin)
      if (!result) {
        const roles: RoleData[] =
          rolesRes.status === "fulfilled"
            ? rolesRes.value.data?.data ?? rolesRes.value.data ?? []
            : [];
        const myRole = roles.find(
          (r) => normalizeRole(r.name) === normalizeRole(me?.role),
        );

        if (process.env.NODE_ENV !== "production" && !myRole) {
          console.warn(
            `[Permission] Role "${me?.role}" tidak ditemukan di /admin/roles.`,
            "Role tersedia:",
            roles.map((r) => r.name),
          );
        }
        result = buildPermsStr(myRole?.privileges ?? [], allPrivileges);
      }

      if (process.env.NODE_ENV !== "production") {
        const missing = (Object.entries(MODULE) as [string, number][])
          .filter(
            ([, idx]) =>
              allPrivileges.length > 0 &&
              !allPrivileges.some((p) => p.index === idx),
          )
          .map(([k, idx]) => `${k} (${idx})`);
        if (missing.length) {
          console.warn(
            "[Permission] Privilege berikut ada di MODULE frontend tapi belum ada di katalog /admin/privileges backend:",
            missing,
          );
        }
      }

      setPermsStr(result.permsStr);
      setModuleIndex(result.moduleIndex);
      setLoading(false);
    } catch {
      if (requestId === requestIdRef.current) clear();
    }
  }, [clear]);

  // Event listeners: token baru tersimpan (login/MFA sukses) atau sesi habis.
  useEffect(() => {
    const onLogin = () => refresh();
    const onExpired = () => clear();
    window.addEventListener("auth-login", onLogin);
    window.addEventListener("session-expired", onExpired);
    return () => {
      window.removeEventListener("auth-login", onLogin);
      window.removeEventListener("session-expired", onExpired);
    };
  }, [refresh, clear]);

  // Pemicu utama: mount awal dan setiap perpindahan pathname
  // (publik → clear; privat tanpa profil → refresh; privat→privat → skip).
  useEffect(() => {
    if (isPublicPath(pathname)) {
      clear();
    } else if (!profile) {
      refresh();
    }
  }, [pathname, profile, refresh, clear]);

  const can = useCallback(
    (
      nameOrModule: string | number,
      required: number = PERMISSION.READ,
    ): boolean => {
      const idx =
        typeof nameOrModule === "number"
          ? nameOrModule
          : moduleIndex[nameOrModule.toLowerCase()] ??
            moduleIndex[normalizeKey(nameOrModule)];
      if (!idx) return false;
      return hasPerm(permsStr, idx, required);
    },
    [permsStr, moduleIndex],
  );

  return (
    <PermissionContext.Provider
      value={{ profile, permsStr, moduleIndex, loading, can, refresh }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error(
      "usePermission harus dipakai di dalam <PermissionProvider>",
    );
  }
  return ctx;
}